import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full font-normal transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-gray-text text-white hover:bg-gray-text/90 shadow-md hover:shadow-lg",
        primary:
          "bg-gray-text text-white hover:bg-gray-text/90 shadow-md hover:shadow-lg",
        secondary:
          "bg-gray-text text-white hover:bg-gray-text/90 shadow-md hover:shadow-lg",
        announce:
          "bg-[#6386AC] text-white hover:bg-[#6386AC]/90 shadow-md hover:shadow-lg",
        outline:
          "border border-gray-text bg-white text-gray-text hover:bg-gray-text/5",
        ghost: "hover:bg-gray-light text-gray-text hover:text-gray-text",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        link: "text-gray-text underline-offset-4 hover:underline",
      },
      size: {
        sm: "h-8 px-4 text-sm [&_svg]:size-4",
        default:
          "h-10 lg:h-12 px-4 lg:px-8 text-base lg:text-xl [&_svg]:size-5 lg:[&_svg]:size-6",
        lg: "h-12 lg:h-14 px-6 lg:px-8 text-lg lg:text-xl [&_svg]:size-6 lg:[&_svg]:size-7",
        icon: "h-10 w-10 [&_svg]:size-5",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      leftIcon,
      rightIcon,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      >
        {leftIcon && leftIcon}
        {children}
        {rightIcon && rightIcon}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
